import { jsx as _jsx } from "react/jsx-runtime";
import React, { useRef } from 'react';
import { Overlay as BaseOverlay } from '@restart/ui';
import useMergedRefs from '@restart/hooks/useMergedRefs';
import classNames from 'classnames';
import { Fade } from '../Transitions/Fade';
import { useUpdatePopper } from './useUpdatePopper';
import { getDefaultOffset } from './getDefaultOffset';
/**
 * The Overlay component can be used to create custom overlays. Most often, the OverlayTrigger component is enough, but if more control is needed Overlay can be used directly.
 * 
 * * The Overlay component expects a single child (the overlay to be shown), which can be a React element or a function that returns a React element. The child needs to accept a ref. 
 * * The Overlay component expects a target property, which is a ref to the target around which the overlay will be positioned.
 */ export const Overlay = /*#__PURE__*/ React.forwardRef(({ children, transition = true, popperConfig = {}, show = false, rootClose = false, placement = 'top', ...rest }, ref)=>{
    const popperRef = useRef({});
    const overlayChildRef = useRef(null);
    const mergedRef = useMergedRefs(ref, overlayChildRef);
    const offset = rest.offset ?? getDefaultOffset(overlayChildRef.current);
    const amendedPopperConfig = useUpdatePopper(popperRef, popperConfig, rest.target, show);
    const actualTransition = transition === true ? Fade : transition || undefined;
    return /*#__PURE__*/ _jsx(BaseOverlay, {
        ref: mergedRef,
        show: show,
        rootClose: rootClose,
        offset: offset,
        placement: placement,
        transition: actualTransition,
        popperConfig: amendedPopperConfig,
        ...rest,
        children: (props, meta)=>{
            popperRef.current = meta.popper;
            const childClassName = 'props' in children ? children.props.className : '';
            const childStyle = 'props' in children ? children.props.style : {};
            const injectedProps = {
                ...props,
                placement: meta.popper?.placement ?? meta.placement,
                arrowProps: meta.arrowProps,
                popper: meta.popper,
                show: meta.show,
                className: classNames(childClassName, !transition && meta.show && 'show'),
                style: {
                    ...childStyle,
                    ...props.style
                }
            };
            if (typeof children === 'function') return children(injectedProps);
            else return /*#__PURE__*/ React.cloneElement(children, injectedProps);
        }
    });
});
Overlay.displayName = 'Overlay';
